import { useTranslation } from 'next-i18next';
import { ExclamationmarkSmallIcon, JellyIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { Button, Tooltip, TooltipContent, TooltipProvider } from '@/ui_v3';
import { useGuestPlayJellyBalance } from '../hooks/guestPlayJellyBalance';
import { useMyPoints } from '../hooks/useMyPoints';

/**
 * 아바타 꾸미기 상단 우측의 보유 포인트 칩(학생만). **표시 전용**이다.
 *
 * 데스크탑은 우측 프리뷰 패널, 모바일은 헤더 아래 컨트롤 행에 놓인다.
 * 두 레이아웃이 같은 칩을 써야 해서 컴포넌트로 분리했다.
 * (모바일은 AvatarPreviewPanel 자체를 렌더하지 않아 인라인으로 두면 한쪽에만 남는다)
 *
 * Tailwind preflight 가 `button` 에 `cursor: pointer` 를 깔기 때문에, 누를 수 없는데도
 * 손가락 커서가 떠서 "눌러야 될 것 같은데 아무 동작도 안 됨" 제보가 나왔다.
 * pointer-events-none 으로 호버 자체를 없애고 tabIndex=-1 로 탭 순서에서도 뺀다.
 * (같은 모양의 /profile/edit 칩은 '내 포인트' 로 가는 링크라 반대로 눌리게 두었다.)
 */
export const MyPointsChip = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    user
  } = useUser();
  const {
    data: myPoints
  } = useMyPoints();
  const guestPlayJellyBalance = useGuestPlayJellyBalance();

  // 비로그인도 보여준다(시안 1811:87887). 젤리를 못 받는 상태인 건 학생과 같고,
  // 0 이라는 사실을 봐야 "왜 못 사지"가 풀린다. 숨기면 그 질문이 남는다.
  // 선생님만 뺀다 — 젤리 없이도 꾸밀 수 있어서 잔액이 의미가 없다.
  const isTeacher = !!user && user.type?.toLowerCase() !== 'student';
  if (isTeacher) return null;
  return (
    // 호버하면 젤리를 어디서 얻는지 알려준다(시안 1811:87887). 우하단 팝업은 퀴즈당
    // 1회만 뜨고 사라지므로, 나중에 다시 궁금해진 학생이 찾아올 자리가 여기다.
    //
    // ⚠️ 칩 자체는 `pointer-events-none` 이라 호버를 못 받는다(누를 수 없는데 손가락
    // 커서가 떠서 막아 둔 것). 그래서 트리거를 감싸는 span 에 건다.
    <TooltipProvider delayDuration={100} data-sentry-element="TooltipProvider" data-sentry-component="MyPointsChip" data-sentry-source-file="MyPointsChip.tsx">
      <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="MyPointsChip.tsx">
        <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="MyPointsChip.tsx">
          <span className="inline-flex">
            <Button variant="outlined" intent="neutral" size="md" rounded="full" tabIndex={-1} className="pointer-events-none bg-white shadow-[0px_1px_4px_0px_rgba(28,27,31,0.12),0px_0px_6px_0px_rgba(28,27,31,0.04)]" leftSlot={<JellyIcon size="sm" />} data-sentry-element="Button" data-sentry-source-file="MyPointsChip.tsx">
              {user ? myPoints?.currentBalance ?? 0 : guestPlayJellyBalance?.balance ?? 0}
            </Button>
          </span>
        </Tooltip.Trigger>
        <TooltipContent side="bottom" align="end" arrow leftSlot={<ExclamationmarkSmallIcon size="xs" />} data-sentry-element="TooltipContent" data-sentry-source-file="MyPointsChip.tsx">
          {t('common:avatarDecorate.jellyFromTeacher')}
        </TooltipContent>
      </Tooltip.Root>
    </TooltipProvider>
  );
};